import { useLazyQuery } from '@apollo/client';
import StandardPopup from 'component/StandardPopup';
import { database, isLocalServer } from '../../firebase';

import { GET_RELEASE_FEATURES } from 'graph_ql/release_feature/queries.gql';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState, useAppDispatch } from 'redux/store';
import { User } from 'types/types';
import {
    getReleaseFeatures,
    getReleaseFeaturesVariables,
    getReleaseFeatures_getReleaseFeatures,
} from '__generated__/getReleaseFeatures';
import { UserType } from '__generated__/globalTypes';
import utils from 'utils/utils';
import { SESSION_STORAGE_KEY } from 'utils/constant';
import EventEmitter from 'reactjs-eventemitter';
import { EmitterEvents } from '../../constants/event-emitter';
import AppVersionContent from 'features/appVersion/AppVersionContent';
import { useLocation } from 'react-router-dom';
import { TimedSessionStorage } from 'utils/TimedSessionStorage';
import { PATH } from 'routes/constant';
import { updateIsLinkClicked } from 'redux/chat/chat.slice';

const modalState = {
    button1: 'Update now',
    path: '',
    type: '',
    closeButton: false,
};
export const AppVersionProvider = () => {
    const [modalShow, setModalShow] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date().valueOf());
    const [remind, setRemind] = useState<number | null>(null);
    const [latestVersion, setLatestVersion] = useState(process.env.REACT_APP_WEBAPP_VERSION);
    const [releaseFeatures, setReleaseFeatures] = useState<getReleaseFeatures_getReleaseFeatures[]>([]);
    const shouldRemind = useMemo(() => (remind === null ? null : currentTime >= remind), [currentTime, remind]);
    const storeUrl = useSelector((state: RootState) => state.widget.store?.url);
    const widgetOpenState = useSelector((state: RootState) => state.widget.isOpened);
    const location = useLocation();
    const dispatch = useAppDispatch();

    const handleWidgetNewVersion = (data: any) => {
        if (data.version !== process.env.REACT_APP_WEBAPP_VERSION) {
            if (utils.isWidgetOpened()) {
                location.pathname !== PATH.PDP_WIDGET && setModalShow(true);
            } else {
                window.parent.postMessage(
                    {
                        reloadPage: true,
                        path: location.pathname,
                        search: location.search,
                        isMessageFromTaffi: true,
                        latestVersion: data.version,
                    },
                    storeUrl,
                );
            }
        }
    };

    const handleNonWidgetNewVersion = (data: any) => {
        if (data.version !== process.env.REACT_APP_WEBAPP_VERSION) {
            setModalShow(true);
        }
    };

    useEffect(() => {
        const fetchLatestVersion = async () => {
            const newVersion = await database.realtimeDatabase.ref('config').child('version').get();
            const versionValue = newVersion.val();
            setLatestVersion(versionValue);

            // Check if there's a new version available on initial load
            if (versionValue && versionValue !== process.env.REACT_APP_WEBAPP_VERSION) {
                console.log('%cNEW VERSION AVAILABLE ON INITIAL LOAD', 'color: red');
                console.log(versionValue);

                // Trigger the version check logic
                if (utils.isAppInWidgetMode()) {
                    handleWidgetNewVersion({ version: versionValue });
                } else {
                    handleNonWidgetNewVersion({ version: versionValue });
                }
            }
        };

        fetchLatestVersion();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isLocalServer) {
            console.log('appVersionProvider: skip version change subscription.');
            return;
        }
        if (utils.isAppInWidgetMode()) {
            if (storeUrl) {
                EventEmitter.subscribe(EmitterEvents.NEW_VERSION_AVAILABLE, (data) => handleWidgetNewVersion(data));
            }
        } else {
            EventEmitter.subscribe(EmitterEvents.NEW_VERSION_AVAILABLE, (data) => handleNonWidgetNewVersion(data));
        }
        // eslint-disable-next-line
    }, [storeUrl, widgetOpenState]);

    const handleNewVersionCallback = () => {
        database.realtimeDatabase
            .ref('config')
            .child('version')
            .get()
            .then((newValue) => {
                if (utils.isAppInWidgetMode()) {
                    handleWidgetNewVersion(newValue.val());
                } else {
                    handleNonWidgetNewVersion(newValue.val());
                }
            });
    };

    const handleClickClose = useMemo(
        () => () => {
            setModalShow(false);
            setRemind(new Date().valueOf() + 1000 * 60 * 5);
        },
        [],
    );
    const handleAgree = useMemo(
        () => async () => {
            setModalShow(false);
            if (utils.isAppInWidgetMode()) {
                const newVersion = await database.realtimeDatabase.ref('config').child('version').get();
                if (newVersion.val() !== latestVersion) {
                    setLatestVersion(newVersion.val());
                }
                dispatch(updateIsLinkClicked(true));
                window.parent.postMessage(
                    {
                        reloadPage: true,
                        path: location.pathname,
                        search: location.search,
                        isMessageFromTaffi: true,
                        latestVersion: newVersion.val(),
                    },
                    storeUrl,
                );
            } else {
                window.location.reload();
            }
        },
        [latestVersion, location.pathname, location.search, storeUrl, dispatch],
    );

    const [getReleaseFeatures] = useLazyQuery<getReleaseFeatures, getReleaseFeaturesVariables>(GET_RELEASE_FEATURES, {
        fetchPolicy: 'network-only',
    });

    const me = useSelector<RootState, User | null>((state) => state.userDetail.me);

    useEffect(() => {
        if (!process.env.REACT_APP_WEBAPP_VERSION) return;
        const onValueChange = database.realtimeDatabase
            .ref('config')
            .child('version')
            .on('value', async (snapshot) => {
                await verifyReleaseVersionTarget(snapshot?.val());
                TimedSessionStorage.setItemWithExpiration(SESSION_STORAGE_KEY.TAFFI_APP_VERSION, snapshot?.val());
            });
        return () => {
            database.realtimeDatabase.ref('config').child('version').off('value', onValueChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [handleNewVersionCallback]);

    const verifyReleaseVersionTarget = async (version?: string, onFinally?: () => void) => {
        try {
            let existingVersion = TimedSessionStorage.getItemWithExpiration(SESSION_STORAGE_KEY.TAFFI_APP_VERSION);
            if (existingVersion === version || !version) return;
            if (onFinally) {
                console.log('%cNEW VERSION COME!', 'color: red');
            }
            const { data } = await getReleaseFeatures({
                variables: {
                    input: {
                        version,
                    },
                },
            });

            const releaseFeaturesData = data?.getReleaseFeatures?.filter(
                (it) =>
                    it?.version !== process.env.REACT_APP_WEBAPP_VERSION &&
                    me?.userType &&
                    it?.tags.includes(me?.userType as UserType),
            );

            setLatestVersion(version);

            if (releaseFeaturesData && releaseFeaturesData.length !== 0) {
                setReleaseFeatures(releaseFeaturesData as getReleaseFeatures_getReleaseFeatures[]);
            }
        } catch (error) {
            if (error instanceof SyntaxError) {
                console.error('Invalid JSON:', error);
            } else console.log('Error Occured: ', error);
        }

        onFinally && onFinally();
    };

    useEffect(() => {
        if (shouldRemind === false) return;
        if (latestVersion !== process.env.REACT_APP_WEBAPP_VERSION) {
            // fetch version information
            verifyReleaseVersionTarget(latestVersion, handleNewVersionCallback);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [latestVersion, shouldRemind]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            setCurrentTime(new Date().valueOf());
        }, 1000 * 30); // in milliseconds
        return () => clearInterval(intervalId);
    }, []);

    return (
        <>
            <StandardPopup
                show={modalShow}
                onHide={() => setModalShow(false)}
                content={<AppVersionContent releaseFeatures={releaseFeatures} userDetail={me} />}
                button1={modalState.button1}
                // buttonCancel={modalState.buttonCancel}
                // path={modalState.path}
                type={modalState.type as any}
                closeButton={modalState.closeButton}
                onFirstButtonClick={handleAgree}
                onSecondButtonClick={handleClickClose}
            />
        </>
    );
};
